import { LegalContact, WhosMyLawyerContent } from "@amzn/ask-legal-domain";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
    Box,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    CollectionPreferences,
    CollectionPreferencesProps,
    Header,
    Pagination,
    SpaceBetween,
    Table,
    TableProps,
    TextFilter
} from "@amzn/awsui-components-react";
import * as React from "react";
import { LegalContactsPolarisFactory } from "../../factory/polaris/legal-contacts-polaris-factory";
import { LegalContactFactory } from "../../factory/legal-contact-factory";
import { legalContactAriaLabels, legalContactAriaLiveLabels } from "../../i18n/legalContactTableI18nStrings";
import { AppContext } from "../../setup/context";
import { LegalContactConstants } from "../../utils/legal-contact.constant";
import { Preference } from "../../setup/preference";

/**
 * Helper function to get original contacts from expanded rows
 * Extracts unique contacts when rows have been expanded for multi-value fields
 */
function getOriginalContactsFromExpanded(expandedItems: readonly LegalContact.Record[]): LegalContact.Record[] {
    const seen = new Set<string>();
    const originalContacts: LegalContact.Record[] = [];

    expandedItems.forEach(item => {
        const expandedRow = item as LegalContactsPolarisFactory.Table.ExpandedLegalContactRow;
        const contactId = expandedRow._originalContactRef || item.ref.entityRef.entityId;

        // Only add each unique contact once
        if (!seen.has(contactId)) {
            seen.add(contactId);
            originalContacts.push(item);
        }
    });

    return originalContacts;
}

export const LegalContactTable = (props: {
    items: LegalContact.Record[];
    loading: boolean;
    onHeaderAction?: (action: string, item?: LegalContact.Record) => void;
    reloadData?: () => void;
    defaultPreference?: Readonly<CollectionPreferencesProps.Preferences<LegalContact.Record>>;
    defaultSortState?: Readonly<TableProps.SortingState<LegalContact.Record>>;
    defaultFilteringText?: Readonly<string>;
    isEditor?: boolean;
    isLegalContactOrManager?: string;
    headerContent?: JSX.Element | string;
    headerDescription?: JSX.Element | string;
    content?: WhosMyLawyerContent;
    enableLog?: boolean;
    onSyncContacts?: () => void;
    showSyncButton?: boolean;
}) => {
    const context = React.useContext(AppContext);
    const [preferences, setPreferences] = React.useState<CollectionPreferencesProps.Preferences<LegalContact.Record>>(
        props.defaultPreference || LegalContactsPolarisFactory.DEFAULT_PREFERENCES
    );
    const columnDefs = LegalContactsPolarisFactory.Table.toColumnDefinition(props.content);
    const sortingColumn = columnDefs
        .find(column => column.sortingField === (
            props.defaultSortState?.sortingColumn?.sortingField ||
            LegalContactsPolarisFactory.DEFAULT_SORTING_STATE.sortingColumn.sortingField
        ));

    const filteringFunction = (item: LegalContact.Record, filteringText: string, filteringFields: string[]) => {
        if (LegalContactsPolarisFactory.Filters.applySpecialAttributeFilter(filteringText, item)) return true;

        const expandedRow = item as LegalContactsPolarisFactory.Table.ExpandedLegalContactRow;
        if (expandedRow._primaryFieldKey) {
            const expandedValue = expandedRow._expandedPrimaryValue;

            // Check if expanded value matches
            let expandedMatches = false;
            if (expandedValue) {
                if (typeof expandedValue === "object" && "displayValue" in expandedValue) {
                    expandedMatches = expandedValue.displayValue?.toLowerCase().includes(filteringText.toLowerCase()) || false;
                } else if (typeof expandedValue === "object" && "name" in expandedValue) {
                    expandedMatches = expandedValue.name?.toLowerCase().includes(filteringText.toLowerCase()) || false;
                } else {
                    expandedMatches = String(expandedValue).toLowerCase().includes(filteringText.toLowerCase());
                }
            }

            if (expandedMatches) return true;

            // Exclude the primary expanded field from other checks to avoid duplicate matches
            let fieldToExclude: string;
            if (expandedRow._primaryFieldKey.startsWith(LegalContactConstants.ADDITIONAL_FIELD_PREFIX)) {
                fieldToExclude = "additionalFields";
            } else {
                fieldToExclude = LegalContactConstants.FIELD_KEY_TO_PROPERTY[expandedRow._primaryFieldKey] || expandedRow._primaryFieldKey;
            }
            const fieldsToCheck = LegalContactsPolarisFactory.Filters.getDefaultFilterFields().filter(f => f !== fieldToExclude);
            return LegalContactsPolarisFactory.Filters.applyDefaultFilter(filteringText, item, fieldsToCheck);
        }

        return LegalContactsPolarisFactory.Filters.applyDefaultFilter(
            filteringText,
            item,
            LegalContactsPolarisFactory.Filters.getDefaultFilterFields()
        );
    };

    const collection = useCollection(
        props.items,
        {
            filtering: {
                filteringFunction: filteringFunction,
                defaultFilteringText: props.defaultFilteringText,
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: {
                    sortingColumn: sortingColumn,
                    isDescending: props.defaultSortState ?
                        props.defaultSortState.isDescending :
                        LegalContactsPolarisFactory.DEFAULT_SORTING_STATE.isDescending
                }
            },
            selection: {
                trackBy: (item) => {
                    const expandedRow = item as LegalContactsPolarisFactory.Table.ExpandedLegalContactRow;
                    return expandedRow._uniqueRowId || item.ref.entityRef.entityId;
                }
            },
        }
    );

    // Export handler - exports all contacts regardless of active filters
    const handleExport = React.useCallback(() => {
        const contactsToExport = getOriginalContactsFromExpanded(props.items);
        LegalContactFactory.exportToFile({
            items: contactsToExport,
            additionalFieldsConfig: props.content?.dataOptions?.additionalFieldsConfig,
            filePrefix: "Legal_Contacts"
        });
    }, [props.items, props.content]);

    // Table react components
    const buttonDropdownItems: ButtonDropdownProps.Item[] = [
        {
            id: "update",
            text: "Update Legal Contact",
            disabled: collection.collectionProps.selectedItems.length < 1
        },
        {
            id: "delete",
            text: "Delete Legal Contact",
            disabled: collection.collectionProps.selectedItems.length < 1
        }
    ];

    const headerActions = <SpaceBetween size="s" direction="horizontal">
        {props.reloadData && <Button
            variant="icon"
            iconName="refresh"
            loading={props.loading}
            onClick={props.reloadData}
        />}
        <Button
            iconName="download"
            onClick={handleExport}
            disabled={props.loading || props.items.length === 0}
        >
            Export
        </Button>
        {props.showSyncButton && <Button
            onClick={props.onSyncContacts}
            loading={props.loading}
        >
            Sync contacts
        </Button>}
        {(props.isEditor || props.isLegalContactOrManager) && <ButtonDropdown
            items={buttonDropdownItems}
            onItemClick={(e) => props.onHeaderAction(
                e.detail.id,
                collection.collectionProps.selectedItems?.[0]
            )}
            loading={props.loading}
            disabled={collection.collectionProps.selectedItems.length < 1}
        >
            Actions
        </ButtonDropdown>}
        {props.isEditor && <Button
            iconName="add-plus"
            variant="primary"
            onClick={() => props.onHeaderAction("create")}
            loading={props.loading}
        >
            Add Legal Contact
        </Button>}
    </SpaceBetween>;

    const header = <Header
        actions={headerActions}
        counter={`(${collection.items.length}/${props.items.length})${props.loading ? " Retrieving..." : ""}`}
        description={props.headerDescription}
    >
        {props.headerContent}
    </Header>;

    const empty = <Box textAlign="center" color="text-body-secondary" padding={{ top: "xxl" }}>
        <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            No legal contacts found.
        </Box>
    </Box>;

    const tablePreferences = <CollectionPreferences
        preferences={preferences}
        onConfirm={({ detail }) => setPreferences(detail)}
        pageSizePreference={{
            options: LegalContactsPolarisFactory.DEFAULT_PAGE_SIZE_OPTIONS,
            title: "Number of contacts per page"
        }}
        stripedRowsPreference={{
            label: "Striped rows",
            description: "Select to add alternating shaded rows"
        }}
        contentDisplayPreference={{
            options: LegalContactsPolarisFactory.Table.getColumnDisplayOptions(props.content),
            title: "Column preferences",
            description: "Customize the columns visibility and order"
        }}
        cancelLabel="Clear"
        confirmLabel="Apply"
        title="Preferences"
        disabled={props.loading}
    />;

    const textFilter = <TextFilter
        {...collection.filterProps}
        filteringAriaLabel="Search contacts"
        filteringPlaceholder="Search contacts"
        filteringClearAriaLabel="Clear"
        countText={`${collection.filteredItemsCount} contacts`}
        disabled={props.loading}
    />;

    return (
        <div className="legal-contacts-table">
            <Table
                {...collection.collectionProps}
                columnDefinitions={
                    LegalContactsPolarisFactory.Table.toColumnDefinition(
                        props.content
                    )
                }
                columnDisplay={preferences.contentDisplay}
                items={collection.items}
                selectionType={(props.isEditor || props.isLegalContactOrManager) ? "single" : undefined}
                trackBy={(item) => {
                    const expandedRow = item as LegalContactsPolarisFactory.Table.ExpandedLegalContactRow;
                    return expandedRow._uniqueRowId || item.ref.entityRef.entityId;
                }}
                ariaLabels={legalContactAriaLabels}
                renderAriaLive={legalContactAriaLiveLabels}
                variant="container"
                stickyHeader={false}
                resizableColumns={false}
                wrapLines={preferences.wrapLines}
                stripedRows={preferences.stripedRows}
                contentDensity={preferences.contentDensity || "compact"}
                stickyColumns={preferences.stickyColumns || { first: 1, last: 0 }}
                header={props.headerContent && header}
                empty={empty}
                loadingText="Loading contacts"
                loading={props.loading && collection.items.length === 0}
                filter={textFilter}
                pagination={<Pagination {...collection.paginationProps} />}
                preferences={tablePreferences}
                isItemDisabled={item => (!props.isEditor && props.isLegalContactOrManager) && (
                    item.legalContactUser.id !== props.isLegalContactOrManager &&
                    item.legalContactManager.id !== props.isLegalContactOrManager
                )}
            />
        </div>
    );
};